/**
 * Review links of an ad (docs/SPEC.md §14.5): the owner makes a link (the
 * URL is shown once), with comments and, if they choose, Allow reuse (another
 * organisation may take the film's scenes, §12); lists and revokes links, and
 * reads and hides the comments.
 */
import { useState } from "react";
import {
  useCreateShareLink,
  useHideShareComment,
  useRevokeShareLink,
  useShareIndex,
} from "../api/share";
import {
  SHARE_DEFAULT_DAYS,
  SHARE_MAX_DAYS,
  type ShareLink,
} from "../api/types";
import { useMe } from "../api/me";
import { useOutputs } from "../api/outputs";
import { SHARE } from "../copy/outputs";
import { Button, Card, Field, Input, Pill } from "../ui";
import { ErrorLine } from "../ui/ErrorLine";
import { formatDate, formatDuration, formatRelative } from "../ui/format";
import { useNow } from "../ui/useNow";

export function linkStatus(link: ShareLink): "active" | "revoked" | "expired" {
  if (link.revoked_at) return "revoked";
  return link.active ? "active" : "expired";
}

/** The clipboard when the page has one; false when it is unavailable or
 *  refused, so the URL stays selectable in its box. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function NewLink({ url, onDone }: { url: string; onDone: () => void }) {
  const [copied, setCopied] = useState<"idle" | "copied" | "failed">("idle");
  return (
    <div
      className="flex flex-col gap-2 rounded border border-brand-tint bg-brand-tint/40 p-3"
      role="status"
      data-testid="new-link"
    >
      <p className="m-0 text-sm font-semibold text-slate-800">{SHARE.ready}</p>
      <div className="flex flex-wrap gap-2">
        <Input
          readOnly
          value={url}
          aria-label={SHARE.linkLabel}
          className="min-w-0 flex-1 font-mono text-xs"
          onFocus={(e) => e.currentTarget.select()}
        />
        <Button
          type="button"
          size="sm"
          variant="primary"
          onClick={() => {
            void copyText(url).then((ok) =>
              setCopied(ok ? "copied" : "failed"),
            );
          }}
        >
          {copied === "copied" ? SHARE.copied : SHARE.copy}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          {SHARE.done}
        </Button>
      </div>
      {copied === "failed" ? (
        <p className="m-0 text-xs text-slate-600">{SHARE.copyFailed}</p>
      ) : null}
    </div>
  );
}

/** The panel once the ad has a finished film to show (a review page plays the
 *  newest real-time film or ray-traced final); nothing before. */
export function ShareCard({ adId }: { adId: string }) {
  const outputs = useOutputs(adId);
  const now = useNow(60_000);
  const ready = (outputs.data?.items ?? []).some(
    (o) => (o.kind === "rt" || o.kind === "final") && o.state === "done",
  );
  return ready ? <SharePanel adId={adId} now={now} /> : null;
}

export function SharePanel({ adId, now }: { adId: string; now: number }) {
  const share = useShareIndex(adId);
  const create = useCreateShareLink(adId);
  const revoke = useRevokeShareLink(adId);
  const hide = useHideShareComment(adId);
  const [days, setDays] = useState(String(SHARE_DEFAULT_DAYS));
  const [allowComments, setAllowComments] = useState(true);
  const [reuse, setReuse] = useState(false);
  // Allow reuse is offered only while shared templates are on (P27-T01)
  const reusable = useMe().data?.features?.templates_shared === true;
  const [fresh, setFresh] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const daysNumber = Number(days);
  const daysValid =
    Number.isInteger(daysNumber) &&
    daysNumber >= 1 &&
    daysNumber <= SHARE_MAX_DAYS;

  const onCreate = () => {
    if (!daysValid || create.isPending) return;
    create.mutate(
      {
        expires_in_days: daysNumber,
        allow_comments: allowComments,
        reuse: reusable && reuse,
      },
      { onSuccess: (link) => setFresh(link.url) },
    );
  };

  const links = share.data?.links ?? [];
  const comments = share.data?.comments ?? [];
  const failure = revoke.error ?? hide.error;

  return (
    <Card className="flex flex-col gap-4 p-6" data-testid="share-panel">
      <div>
        <h2 className="tb-h-row m-0">{SHARE.title}</h2>
        <p className="m-0 mt-1 text-sm text-slate-500">{SHARE.meta}</p>
      </div>
      {fresh ? (
        <NewLink url={fresh} onDone={() => setFresh(null)} />
      ) : (
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            onCreate();
          }}
        >
          <Field
            label={SHARE.days}
            error={daysValid ? null : `1 to ${SHARE_MAX_DAYS} days`}
          >
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              max={SHARE_MAX_DAYS}
              value={days}
              className="w-24"
              onChange={(e) => setDays(e.target.value)}
            />
          </Field>
          <label className="flex items-center gap-2 pb-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={allowComments}
              onChange={(e) => setAllowComments(e.target.checked)}
            />
            {SHARE.comments}
          </label>
          {reusable ? (
            <label
              className="flex items-center gap-2 pb-2 text-sm text-slate-700"
              title={SHARE.reuseHint}
            >
              <input
                type="checkbox"
                checked={reuse}
                onChange={(e) => setReuse(e.target.checked)}
              />
              {SHARE.reuse}
            </label>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            disabled={!daysValid || create.isPending}
            aria-busy={create.isPending}
          >
            {create.isPending ? SHARE.creating : SHARE.create}
          </Button>
        </form>
      )}
      {create.isError ? <ErrorLine error={create.error} /> : null}
      {failure ? <ErrorLine error={failure} /> : null}

      {links.length > 0 ? (
        <ul
          className="m-0 flex list-none flex-col gap-2 p-0 text-sm"
          aria-label={SHARE.links}
        >
          {links.map((link) => {
            const status = linkStatus(link);
            return (
              <li
                key={link.id}
                className="flex flex-wrap items-center gap-2"
                data-testid={`link-${link.id}`}
                data-status={status}
              >
                <Pill
                  size="sm"
                  tone={
                    status === "active"
                      ? "success"
                      : status === "revoked"
                        ? "danger"
                        : "neutral"
                  }
                >
                  {SHARE.status[status]}
                </Pill>
                <span className="text-slate-700">
                  {status === "active" ? SHARE.expires : SHARE.expired}{" "}
                  {formatDate(link.expires_at)}
                </span>
                <span className="text-xs text-slate-500">
                  · {link.allow_comments ? SHARE.commentsOn : SHARE.commentsOff}
                  {link.reuse ? ` · ${SHARE.reuseOn}` : ""} ·{" "}
                  {formatRelative(link.created_at, now)}
                </span>
                {status === "active" ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="ml-auto"
                    disabled={busy !== null}
                    aria-busy={busy === link.id}
                    aria-label={`${SHARE.revoke} ${formatRelative(link.created_at, now)}`}
                    onClick={() => {
                      setBusy(link.id);
                      revoke.mutate(link.id, {
                        onSettled: () => setBusy(null),
                      });
                    }}
                  >
                    {busy === link.id ? SHARE.revoking : SHARE.revoke}
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : share.data ? (
        <p className="m-0 text-sm text-slate-500">{SHARE.none}</p>
      ) : null}

      {comments.length > 0 ? (
        <div>
          <h3 className="m-0 mb-2 text-sm font-semibold text-slate-800">
            {SHARE.commentsTitle}
          </h3>
          <ul
            className="m-0 flex list-none flex-col gap-3 p-0 text-sm"
            aria-label={SHARE.commentsTitle}
          >
            {comments.map((c) => (
              <li
                key={c.id}
                className="flex flex-col gap-1"
                data-testid={`comment-${c.id}`}
              >
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-semibold text-slate-800">
                    {c.author_name}
                  </span>
                  {c.t !== null ? (
                    <Pill size="sm" tone="purple">
                      {formatDuration(c.t)}
                    </Pill>
                  ) : null}
                  <span className="text-xs text-slate-500">
                    {formatRelative(c.created_at, now)}
                  </span>
                </div>
                <p className="m-0 whitespace-pre-wrap text-slate-700">
                  {c.body}
                </p>
                <div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    disabled={busy !== null}
                    aria-busy={busy === c.id}
                    aria-label={`${SHARE.hide} ${c.author_name}`}
                    onClick={() => {
                      setBusy(c.id);
                      hide.mutate(c.id, { onSettled: () => setBusy(null) });
                    }}
                  >
                    {busy === c.id ? SHARE.hiding : SHARE.hide}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}
